/**
 * 各资源类型配置区完成度（纯函数，读 basicForm captureSnapshot）。
 * 与 validate() 必填项对齐，供顶栏待完成数与 Card 角标实时刷新。
 */
import { GENERIC_RESOURCE_BASIC_FORM_MODE, resolveGenericResourceTypeEntry } from './genericResourceFormRegistry'
import { isNonNegativeInt, parseNonNegativeInt } from './basicForms/badgeIntegerFields'
import {
  parseRepeatCount,
  resolveActionPlayUnitSeconds,
} from './basicForms/workoutActionBindingCalculator'

function filled(value) {
  return String(value == null ? '' : value).trim() !== ''
}

function parseMet(value) {
  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed) || parsed < 0.1) {
    return null
  }
  return Math.round(parsed * 10) / 10
}

function evalWorkoutAction(snapshot) {
  const data = snapshot || {}
  const pending = []
  if (!filled(data.display_name)) {
    pending.push({ id: 'display_name', label: '展示名称', sectionId: 'typeConfig' })
  }
  if (!filled(data.difficulty)) {
    pending.push({ id: 'difficulty', label: '难度', sectionId: 'typeConfig' })
  }
  // 完成度读表单 snapshot（字段名 met）；旧 calories 仅在 loadFromExta 回显时兼容
  if (parseMet(data.met) == null) {
    pending.push({ id: 'met', label: 'MET', sectionId: 'typeConfig' })
  }
  if (typeof data.video_loop !== 'boolean') {
    pending.push({ id: 'video_loop', label: '视频循环', sectionId: 'typeConfig' })
  }
  return pending
}

function evalWorkoutMusic(snapshot) {
  const data = snapshot || {}
  const pending = []
  if (!filled(data.display_name)) {
    pending.push({ id: 'display_name', label: '展示名称', sectionId: 'typeConfig' })
  }
  if (typeof data.loopable !== 'boolean') {
    pending.push({ id: 'loopable', label: '循环播放', sectionId: 'typeConfig' })
  }
  return pending
}

function evalWorkoutVoice(snapshot) {
  const data = snapshot || {}
  const pending = []
  if (!filled(data.display_name)) {
    pending.push({ id: 'display_name', label: '展示名称', sectionId: 'typeConfig' })
  }
  if (!filled(data.gender)) {
    pending.push({ id: 'gender', label: '性别', sectionId: 'typeConfig' })
  }
  if (!filled(data.language)) {
    pending.push({ id: 'language', label: '语言', sectionId: 'typeConfig' })
  }
  return pending
}

function evalWorkoutCollection(snapshot) {
  const data = snapshot || {}
  const pending = []
  if (!filled(data.display_name)) {
    pending.push({ id: 'display_name', label: '展示名称', sectionId: 'typeConfig' })
  }
  if (!filled(data.difficulty)) {
    pending.push({ id: 'difficulty', label: '难度', sectionId: 'typeConfig' })
  }
  const actions = Array.isArray(data.actions) ? data.actions : []
  if (!actions.length) {
    pending.push({ id: 'actions', label: '动作编排', sectionId: 'typeConfig' })
  } else if (actions.some((row) => !filled(row && row.resource_id))) {
    pending.push({ id: 'actions', label: '动作编排（须选择资源）', sectionId: 'typeConfig' })
  } else if (actions.some((row) => parseRepeatCount(row && row.repeat_count, 0) < 1)) {
    // 与 validateWorkoutCollectionBindings 对齐：repeat 从 0 起配，未配满不能标「已完成」
    pending.push({ id: 'actions', label: '动作编排（循环次数须至少为 1）', sectionId: 'typeConfig' })
  } else if (
    actions.some((row) => {
      if (resolveActionPlayUnitSeconds(row) != null) {
        return false
      }
      // 无 ref_meta 的落库态：允许用已写入的 duration_seconds 判定可编排
      const duration = Number(row && row.duration_seconds)
      return !(Number.isFinite(duration) && duration > 0)
    })
  ) {
    pending.push({ id: 'actions', label: '动作编排（须有可计算的播放时长）', sectionId: 'typeConfig' })
  }
  return pending
}

/** 白噪音字段均为选填，配置区视为始终可完成 */
function evalSounds() {
  return []
}

/**
 * 与 BadgeBasicForm.validate 对齐：描述 / 等级 / 积分均必填；等级 0～6。
 */
const BADGE_LEVEL_MAX = 6

function evalBadge(snapshot) {
  const data = snapshot || {}
  const pending = []
  if (!filled(data.badgeDescription)) {
    pending.push({ id: 'badgeDescription', label: '徽章描述', sectionId: 'typeConfig' })
  }
  const level = parseNonNegativeInt(data.badgeLevel)
  if (level == null || level > BADGE_LEVEL_MAX) {
    pending.push({ id: 'badgeLevel', label: '等级（0～6）', sectionId: 'typeConfig' })
  }
  if (!isNonNegativeInt(data.badgePoints)) {
    pending.push({ id: 'badgePoints', label: '积分', sectionId: 'typeConfig' })
  }
  return pending
}

/**
 * 与 ArticleBasicForm.validate 对齐：文章标题必填，写入 exta.articleTitle 下发 App。
 */
/** 设计师选填，stickers 类型是保存后才写上的结果，这一区没有必填项。 */
function evalSticker() {
  return []
}

function evalArticle(snapshot) {
  const data = snapshot || {}
  const pending = []
  if (!filled(data.articleTitle)) {
    pending.push({ id: 'articleTitle', label: '文章标题', sectionId: 'typeConfig' })
  }
  return pending
}

function evalSchema(assetType, snapshot) {
  const data = snapshot || {}
  const pending = []
  const sectionId = 'schema'
  if (assetType === 'coupon(优惠券)') {
    if (!filled(data.brand)) {
      pending.push({ id: 'brand', label: 'brand', sectionId })
    }
    if (!filled(data.link)) {
      pending.push({ id: 'link', label: 'link', sectionId })
    } else if (!/^https?:\/\/.+/i.test(String(data.link))) {
      pending.push({ id: 'link', label: 'link（有效网址）', sectionId })
    }
    return pending
  }
  if (assetType === 'web(网页)') {
    if (!filled(data.link)) {
      pending.push({ id: 'link', label: 'link', sectionId })
    } else if (!/^https?:\/\/.+/i.test(String(data.link))) {
      pending.push({ id: 'link', label: 'link（有效网址）', sectionId })
    }
  }
  return pending
}

function evalAiAssistant(snapshot) {
  const data = snapshot || {}
  const pending = []
  if (!filled(data.roleName)) {
    pending.push({ id: 'roleName', label: '角色名称', sectionId: 'typeConfig' })
  }
  if (!filled(data.llmModel)) {
    pending.push({ id: 'llmModel', label: 'Model', sectionId: 'typeConfig' })
  }
  if (!filled(data.prompt)) {
    pending.push({ id: 'prompt', label: 'Prompt', sectionId: 'typeConfig' })
  }
  if (!filled(data.avatar)) {
    pending.push({ id: 'avatar', label: 'Avatar', sectionId: 'typeConfig' })
  }
  return pending
}

/**
 * @returns {{ kind: 'none'|'typeConfig'|'schema', sectionId: string|null }}
 */
export function resolveConfigSectionKind(assetType) {
  const entry = resolveGenericResourceTypeEntry(assetType)
  if (!entry || !assetType) {
    return { kind: 'none', sectionId: null }
  }
  if (entry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.SCHEMA) {
    return { kind: 'schema', sectionId: 'schema' }
  }
  if (
    entry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.WORKOUT ||
    entry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.SOUNDS ||
    entry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.AI_ASSISTANT
  ) {
    return { kind: 'typeConfig', sectionId: 'typeConfig' }
  }
  return { kind: 'none', sectionId: null }
}

/**
 * @param {string} assetType
 * @param {object} snapshot basicForm.captureSnapshot()
 * @returns {{ complete: boolean, pending: Array<{id:string,label:string,sectionId:string}> }}
 */
export function evaluateTypeConfigCompletion(assetType, snapshot) {
  const entry = resolveGenericResourceTypeEntry(assetType)
  if (!entry || !assetType) {
    return { complete: true, pending: [] }
  }

  const section = resolveConfigSectionKind(assetType)
  if (section.kind === 'none') {
    return { complete: true, pending: [] }
  }

  let pending = []
  const formName = entry.basicForm
  if (formName === 'WorkoutActionBasicForm') {
    pending = evalWorkoutAction(snapshot)
  } else if (formName === 'WorkoutMusicBasicForm') {
    pending = evalWorkoutMusic(snapshot)
  } else if (formName === 'WorkoutVoiceBasicForm') {
    pending = evalWorkoutVoice(snapshot)
  } else if (formName === 'WorkoutCollectionBasicForm') {
    pending = evalWorkoutCollection(snapshot)
  } else if (formName === 'SoundsBasicForm') {
    pending = evalSounds(snapshot)
  } else if (formName === 'SchemaBasicForm') {
    pending = evalSchema(assetType, snapshot)
  } else if (formName === 'AiAssistantBasicForm') {
    pending = evalAiAssistant(snapshot)
  } else if (formName === 'BadgeBasicForm') {
    pending = evalBadge(snapshot)
  } else if (formName === 'ArticleBasicForm') {
    pending = evalArticle(snapshot)
  } else if (formName === 'StickerBasicForm') {
    pending = evalSticker()
  }

  if (section.kind === 'schema') {
    pending = pending.map((item) => ({ ...item, sectionId: 'schema' }))
  }

  // 有配置表单但未识别到具体求值器时，不能默认 complete（否则新类型会一选就「已完成」）
  const knownForm =
    formName === 'WorkoutActionBasicForm' ||
    formName === 'WorkoutMusicBasicForm' ||
    formName === 'WorkoutVoiceBasicForm' ||
    formName === 'WorkoutCollectionBasicForm' ||
    formName === 'SoundsBasicForm' ||
    formName === 'SchemaBasicForm' ||
    formName === 'AiAssistantBasicForm' ||
    formName === 'BadgeBasicForm' ||
    formName === 'ArticleBasicForm' ||
    formName === 'StickerBasicForm'
  if (!knownForm && section.kind !== 'none') {
    pending = [
      {
        id: 'typeConfigUnknown',
        label: '资源配置（待完善判定）',
        sectionId: section.sectionId || 'typeConfig',
      },
    ]
  }

  return {
    complete: pending.length === 0,
    pending,
    /** 配置区没有必填项时，壳层写「选填」，避免一选类型就出现「已完成」。 */
    optionalOnly: formName === 'SoundsBasicForm' || formName === 'StickerBasicForm',
  }
}
